import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { devtools } from "zustand/middleware";
import { FormService } from "../services/form.service";
import type { ButtonAnimationType } from "@codway-dev/shared";

// ---------- Types ----------

export interface ButtonSettings {
  text: string;
  subtitle: string;
  animation: ButtonAnimationType;
  icon: string;
  icon_size: number;
  icon_color: string;
  sticky_position: "bottom" | "top" | "none";
  bg_color: string;
  text_color: string;
  font_size: number;
  border_radius: number;
  border_width: number;
  border_color: string;
  shadow: number;
  enable_sticky_mobile: boolean;
}

export const DEFAULT_BUTTON_SETTINGS: ButtonSettings = {
  text: "Pagar Contra Reembolso",
  subtitle: "",
  animation: "none",
  icon: "cart",
  icon_size: 20,
  icon_color: "",
  sticky_position: "bottom",
  bg_color: "#000000",
  text_color: "#ffffff",
  font_size: 16,
  border_radius: 4,
  border_width: 0,
  border_color: "#000000",
  shadow: 1,
  enable_sticky_mobile: true,
};

// ---------- Store ----------

interface ButtonEditorState {
  settings: ButtonSettings;
  initialSettings: ButtonSettings | null;
  /** Full design.settings from the backend — preserved to avoid overwriting on save */
  _fullDesignSettings: Record<string, unknown> | null;
  _formId: string | null;
  _templateId: string | null;
  isSaving: boolean;
  isLoading: boolean;
  error: string | null;
}

interface ButtonEditorActions {
  loadFromBackend: () => Promise<void>;
  updateSetting: <K extends keyof ButtonSettings>(
    key: K,
    value: ButtonSettings[K]
  ) => void;
  save: () => Promise<void>;
  discardChanges: () => void;
}

type ButtonEditorStore = ButtonEditorState & ButtonEditorActions;

export const selectButtonIsDirty = (state: ButtonEditorStore) => {
  if (!state.initialSettings) return false;
  return (
    JSON.stringify(state.settings) !== JSON.stringify(state.initialSettings)
  );
};

export const useButtonEditorStore = create<ButtonEditorStore>()(
  devtools(
    immer((set, get) => ({
      settings: DEFAULT_BUTTON_SETTINGS,
      initialSettings: null,
      _fullDesignSettings: null,
      _formId: null,
      _templateId: null,
      isSaving: false,
      isLoading: false,
      error: null,

      loadFromBackend: async () => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });
        try {
          const formData = await FormService.getForm();
          const designSettings = (formData.design.settings ?? {}) as Record<
            string,
            unknown
          >;
          const saved = (designSettings.button_settings ??
            {}) as Partial<ButtonSettings>;

          set((state) => {
            state._formId = formData.id;
            state._templateId = formData.design.template_id;
            state._fullDesignSettings = designSettings;
            state.settings = { ...DEFAULT_BUTTON_SETTINGS, ...saved };
            state.initialSettings = JSON.parse(JSON.stringify(state.settings));
            state.isLoading = false;
          });
        } catch (err) {
          set((state) => {
            state.error =
              err instanceof Error ? err.message : "Failed to load settings";
            state.isLoading = false;
          });
        }
      },

      updateSetting: (key, value) =>
        set((state) => {
          (state.settings as Record<string, unknown>)[key] = value;
        }),

      save: async () => {
        set((state) => {
          state.isSaving = true;
        });
        try {
          const { _formId, _templateId, _fullDesignSettings, settings } = get();
          if (!_formId || !_templateId) throw new Error("Form not loaded");

          const mergedDesignSettings = {
            ..._fullDesignSettings,
            button_settings: settings,
          };

          await FormService.saveFormConfig(_formId, {
            design: {
              template_id: _templateId,
              settings: mergedDesignSettings,
            },
          });

          set((state) => {
            state.isSaving = false;
            state._fullDesignSettings = mergedDesignSettings;
            state.initialSettings = JSON.parse(JSON.stringify(state.settings));
          });
        } catch (err) {
          set((state) => {
            state.isSaving = false;
          });
          throw err instanceof Error ? err : new Error("Failed to save");
        }
      },

      discardChanges: () =>
        set((state) => {
          if (state.initialSettings) {
            state.settings = JSON.parse(JSON.stringify(state.initialSettings));
          }
        }),
    })),
    { name: "ButtonEditorStore" }
  )
);
